// =====================================================
// 📁 POSActionButton.tsx
// =====================================================

import React from 'react';

interface Props {
  children: React.ReactNode;

  variant?:
    | 'primary'
    | 'danger'
    | 'success'
    | 'secondary'
    | 'warning'
    | 'dark'
    | 'info';

  disabled?: boolean;

  fullWidth?: boolean;

  large?: boolean;

  onClick?: () => void;
}

const POSActionButton: React.FC<Props> = ({
  children,

  variant = 'primary',

  disabled = false,

  fullWidth = false,

  large = false,

  onClick,
}) => {
  // =====================================================
  // 🔹 Variants
  // =====================================================
  const variants = {
    primary: `
    bg-primary
    text-white

    hover:opacity-90

    shadow-primary/20
  `,

    success: `
    bg-success
    text-white

    hover:opacity-90

    shadow-success/20
  `,

    warning: `
    bg-warning
    text-black

    hover:opacity-90

    shadow-warning/20
  `,

    danger: `
    bg-danger
    text-white

    hover:opacity-90

    shadow-danger/20
  `,

    info: `
    bg-meta-10
    text-white

    hover:opacity-90

    shadow-meta-10/20
  `,

    dark: `
    bg-meta-4
    text-white

    hover:opacity-90

    shadow-meta-4/20
  `,

    secondary: `
    border
    border-stroke

    bg-white
    text-black

    hover:bg-whiten

    dark:border-strokedark
    dark:bg-boxdark
    dark:text-white
    dark:hover:bg-meta-4
  `,
  };

  // =====================================================
  // 🔹 Sizes
  // =====================================================
  const sizeClasses = large
    ? `
      h-[58px]

      rounded-2xl

      px-6

      text-base
      font-semibold

      shadow-4
    `
    : `
      h-[46px]

      rounded-xl

      px-4

      text-sm
      font-medium

      shadow-1
    `;

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`
        inline-flex
        items-center
        justify-center
        gap-2

        transition-all
        duration-200

        ${fullWidth ? 'w-full' : ''}

        ${sizeClasses}

        ${variants[variant]}

        ${
          disabled
            ? `
        cursor-not-allowed

        opacity-50

        grayscale

        hover:scale-100
      `
            : `
        hover:scale-[1.01]
        active:scale-[0.99]
      `
        }
      `}
    >
      {children}
    </button>
  );
};

export default POSActionButton;
